import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { CreditsService } from '@/services/CreditsService';

type CreditsContextValue = {
	totalCredits: number | null;
	initialCredits: number;
	paidCredits: number;
	loading: boolean;
	creditsLoading: boolean;
	setCreditsLoading: (loading: boolean) => void;
	loadCredits: () => Promise<void>;
};

const CreditsContext = createContext<CreditsContextValue | undefined>(undefined);

export const CreditsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { user, getAuthHeader, getUserId } = useAuth();
	const [totalCredits, setTotalCredits] = useState<number | null>(null);
	const [initialCredits, setInitialCredits] = useState<number>(0);
	const [paidCredits, setPaidCredits] = useState<number>(0);
	const [loading, setLoading] = useState(false);
	const [creditsLoading, setCreditsLoading] = useState(false);

	const loadCredits = useCallback(async () => {
		if (!user) {
			setTotalCredits(null);
			setInitialCredits(0);
			setPaidCredits(0);
			return;
		}

		try {
			setLoading(true);
			const userId = getUserId();
			if (!userId) return;

			const authHeader = getAuthHeader();
			const balance = await CreditsService.getBalance(userId, authHeader);
			const initial = balance.initialCredits || 0;
			const paid = balance.credits || 0;
			const total = initial + paid;

			setInitialCredits(initial);
			setPaidCredits(paid);
			setTotalCredits(total);
		} catch (error: unknown) {
			console.error('Error loading credits:', error);
			setTotalCredits(null);
			setInitialCredits(0);
			setPaidCredits(0);
		} finally {
			setLoading(false);
		}
	}, [user, getUserId, getAuthHeader]);

	useEffect(() => {
		loadCredits();
	}, [loadCredits]);

	const value = useMemo<CreditsContextValue>(
		() => ({
			totalCredits,
			initialCredits,
			paidCredits,
			loading,
			creditsLoading,
			setCreditsLoading,
			loadCredits
		}),
		[totalCredits, initialCredits, paidCredits, loading, creditsLoading, loadCredits]
	);

	return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
};

export const useCredits = (): CreditsContextValue => {
	const context = useContext(CreditsContext);
	if (!context) {
		throw new Error('useCredits must be used within CreditsProvider');
	}
	return context;
};
